import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import MathAuthorField from "@/components/MathAuthorField";
import { Plus, Trash2, Save, ArrowLeft, Loader2, Library, X, Search, CheckCircle2 } from "lucide-react";

const emptyMCQ = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
  type: "mcq",
  text: "",
  options: ["", "", "", ""],
  correct_index: 0,
  points: 1,
});
const emptyDescriptive = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
  type: "descriptive",
  text: "",
  points: 2,
  sample_answer: "",
});

export default function ExamEditor() {
  const { examId } = useParams();
  const isNew = !examId || examId === "new";
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("General");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(20);
  const [passing, setPassing] = useState(60);
  const [isPublished, setIsPublished] = useState(true);
  const [classId, setClassId] = useState("");
  const [classes, setClasses] = useState([]);
  const [questions, setQuestions] = useState([emptyMCQ()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showBank, setShowBank] = useState(false);
  const [bankItems, setBankItems] = useState([]);
  const [bankQuery, setBankQuery] = useState("");
  const [bankSelected, setBankSelected] = useState({}); // id -> true

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/exams/${examId}`).then((r) => {
      const e = r.data;
      setTitle(e.title);
      setSubject(e.subject || "General");
      setDescription(e.description || "");
      setDuration(e.duration_min);
      setPassing(e.passing_score);
      setIsPublished(!!e.is_published);
      setClassId(e.class_id || "");
      setQuestions(
        (e.questions || []).map((q) => ({
          type: q.type,
          text: q.text,
          options: q.options || ["", "", "", ""],
          correct_index: q.correct_index ?? 0,
          points: q.points || 1,
          sample_answer: q.sample_answer || "",
        }))
      );
    });
  }, [examId, isNew]);

  const updateQ = (i, patch) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const updateOption = (qi, oi, val) =>
    setQuestions((qs) =>
      qs.map((q, idx) => (idx === qi ? { ...q, options: q.options.map((o, k) => (k === oi ? val : o)) } : q))
    );

  const addQ = (type) =>
    setQuestions((qs) => [...qs, type === "mcq" ? emptyMCQ() : emptyDescriptive()]);

  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const openBank = async () => {
    setBankSelected({});
    setBankQuery("");
    setShowBank(true);
    try {
      const { data } = await api.get("/question-bank");
      setBankItems(data);
    } catch (e) {
      setBankItems([]);
    }
  };

  const filteredBankItems = bankItems.filter(
    (it) =>
      !bankQuery ||
      it.text.toLowerCase().includes(bankQuery.toLowerCase()) ||
      (it.subject || "").toLowerCase().includes(bankQuery.toLowerCase())
  );

  const importSelected = () => {
    const picked = bankItems
      .filter((it) => bankSelected[it.id])
      .map((it) => ({
        type: it.type,
        text: it.text,
        options: it.type === "mcq" ? (it.options || ["", "", "", ""]) : ["", "", "", ""],
        correct_index: it.type === "mcq" ? (it.correct_index ?? 0) : 0,
        sample_answer: it.type === "descriptive" ? (it.sample_answer || "") : "",
        points: it.points || 1,
      }));
    if (picked.length === 0) {
      setShowBank(false);
      return;
    }
    setQuestions((qs) => [...qs, ...picked]);
    setShowBank(false);
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      const payload = {
        title,
        subject,
        description,
        duration_min: Number(duration),
        passing_score: Number(passing),
        is_published: isPublished,
        class_id: classId || "",
        questions: questions.map((q) => ({
          type: q.type,
          text: q.text,
          options: q.type === "mcq" ? q.options : null,
          correct_index: q.type === "mcq" ? Number(q.correct_index) : null,
          sample_answer: q.type === "descriptive" ? q.sample_answer : null,
          points: Number(q.points) || 1,
        })),
      };
      if (isNew) {
        await api.post("/exams", payload);
      } else {
        await api.patch(`/exams/${examId}`, payload);
      }
      nav("/teacher/exams");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={isNew ? "New exam" : "Edit exam"} subtitle="Compose questions, set duration & publish.">
      <button onClick={() => nav("/teacher/exams")} className="btn-ghost text-sm mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-3 gap-6" data-testid="exam-editor">
        {/* Metadata */}
        <div className="qs-card p-6 lg:sticky lg:top-24 self-start">
          <h3 className="font-display text-lg font-bold text-slate-900">Exam details</h3>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder="e.g. Algebra Fundamentals"
                data-testid="editor-title"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                data-testid="editor-subject"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                data-testid="editor-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Duration (min)</label>
                <input
                  type="number"
                  min={1}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="editor-duration"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Passing %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={passing}
                  onChange={(e) => setPassing(e.target.value)}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="editor-passing"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} data-testid="editor-published" />
              Published (students can take it)
            </label>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Assign to class</label>
              <select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                data-testid="editor-class"
              >
                <option value="">Unassigned (available to all students)</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Assigned exams are visible only to students in that class.
              </p>
            </div>
          </div>

          <button onClick={submit} disabled={busy || !title} className="btn-primary w-full mt-6 justify-center disabled:opacity-70" data-testid="editor-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? "Saving…" : "Save exam"}
          </button>
          {err && <p className="mt-3 text-sm text-rose-600" data-testid="editor-error">{err}</p>}
        </div>

        {/* Questions */}
        <div className="lg:col-span-2 space-y-4">
          {questions.map((q, i) => (
            <div key={q._key || q.id || i} className="qs-card p-6" data-testid={`editor-q-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">
                  Q{i + 1} · {q.type === "mcq" ? "Multiple choice" : "Descriptive"}
                </p>
                <button onClick={() => removeQ(i)} className="text-rose-500 hover:text-rose-700" data-testid={`editor-remove-q-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <MathAuthorField
                value={q.text}
                onChange={(v) => updateQ(i, { text: v })}
                rows={3}
                placeholder="Write the question… use $x^2$ for inline or $$…$$ for block math"
                className="mt-3"
                testIdBase={`editor-q-text-${i}`}
              />

              {q.type === "mcq" ? (
                <div className="mt-4 space-y-3">
                  {q.options.map((opt, oi) => (
                    <div key={`${q._key || q.id || i}-opt-${oi}`} className="flex items-start gap-3">
                      <label className="flex items-center gap-2 mt-2 pt-1.5">
                        <input
                          type="radio"
                          name={`correct-${i}`}
                          checked={q.correct_index === oi}
                          onChange={() => updateQ(i, { correct_index: oi })}
                          data-testid={`editor-q-${i}-correct-${oi}`}
                        />
                        <span className="text-xs font-bold text-slate-500 w-5">{String.fromCharCode(65 + oi)}</span>
                      </label>
                      <MathAuthorField
                        value={opt}
                        onChange={(v) => updateOption(i, oi, v)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        multiline={false}
                        compact
                        className="flex-1"
                        testIdBase={`editor-q-${i}-option-${oi}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4">
                  <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Sample answer (for reference)</label>
                  <MathAuthorField
                    value={q.sample_answer || ""}
                    onChange={(v) => updateQ(i, { sample_answer: v })}
                    rows={3}
                    className="mt-1.5"
                    testIdBase={`editor-q-${i}-sample`}
                  />
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Points</label>
                <input
                  type="number"
                  min={1}
                  value={q.points}
                  onChange={(e) => updateQ(i, { points: e.target.value })}
                  className="w-20 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid={`editor-q-${i}-points`}
                />
              </div>
            </div>
          ))}

          <div className="flex gap-3 flex-wrap">
            <button onClick={() => addQ("mcq")} className="btn-ghost text-sm" data-testid="editor-add-mcq">
              <Plus className="w-4 h-4" /> Add MCQ
            </button>
            <button onClick={() => addQ("descriptive")} className="btn-ghost text-sm" data-testid="editor-add-descriptive">
              <Plus className="w-4 h-4" /> Add Descriptive
            </button>
            <button
              type="button"
              onClick={openBank}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100 transition"
              data-testid="editor-open-bank"
            >
              <Library className="w-4 h-4" /> Add from question bank
            </button>
          </div>
        </div>
      </div>

      {/* Bank picker modal */}
      {showBank && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowBank(false)} data-testid="bank-picker-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900">Pick from question bank</h3>
                <p className="text-xs text-slate-500 mt-1">Select questions to add to this exam.</p>
              </div>
              <button onClick={() => setShowBank(false)} className="text-slate-400 hover:text-slate-800" data-testid="bank-picker-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 border-b border-slate-100">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={bankQuery}
                  onChange={(e) => setBankQuery(e.target.value)}
                  placeholder="Search by text or subject…"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="bank-picker-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-2">
              {filteredBankItems.map((it) => {
                const selected = !!bankSelected[it.id];
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setBankSelected({ ...bankSelected, [it.id]: !selected })}
                    className={`w-full text-left p-4 rounded-xl border transition flex items-start gap-3 ${
                      selected ? "bg-indigo-50 border-indigo-500" : "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`bank-picker-item-${it.id}`}
                  >
                    <span
                      className={`mt-0.5 w-5 h-5 rounded grid place-items-center shrink-0 ${
                        selected ? "bg-indigo-600 text-white" : "bg-white border border-slate-300"
                      }`}
                    >
                      {selected && <CheckCircle2 className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.type === "mcq" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
                          {it.type === "mcq" ? "MCQ" : "Descriptive"}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {it.subject} · {it.points} pt
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-slate-800">{it.text}</p>
                    </div>
                  </button>
                );
              })}
              {filteredBankItems.length === 0 && (
                <p className="text-center text-slate-500 py-8" data-testid="bank-picker-empty">
                  No matching questions. Add some in Question bank first.
                </p>
              )}
            </div>
            <div className="p-5 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {Object.values(bankSelected).filter(Boolean).length} selected
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowBank(false)} className="btn-ghost text-sm">Cancel</button>
                <button type="button" onClick={importSelected} className="btn-primary text-sm" data-testid="bank-picker-import">
                  <Plus className="w-4 h-4" /> Add selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
